import React from 'react';

import { useIntl } from 'react-intl-next';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';
import { getUrlMaybe, type GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type {
	ButtonItemConfig,
	ExpandableItemConfig,
	FlyoutItemConfig,
	LinkItemConfig,
	NavItemConfig,
	ObjectListFlyoutItemConfig,
} from '../../../common/types';
import NavItem from '../../../common/ui/nav-item';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../../common/utils/type-guards';

import { messages } from './messages';

export interface MoreMenuItemProps {
	id: string;
	items: Record<string, NavItemConfig>;
	shortcutItems?: Record<string, GlobalShortcutsItem>;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}

export default function MoreMenuItem({
	id,
	items,
	shortcutItems,
	onClick,
	onPeek,
}: MoreMenuItemProps): React.ReactNode {
	const { formatMessage } = useIntl();
	const shortcutItem = shortcutItems?.[id];
	if (shortcutItem) {
		return (
			<LinkMenuItem
				elemBefore={shortcutItem.icon}
				elemAfter={
					<LinkExternalIcon
						size="small"
						label={formatMessage(messages.linkExternalIconLabel)}
						spacing="spacious"
					/>
				}
				onClick={onClick}
				href={getUrlMaybe(shortcutItem) || '#'}
				target="_blank"
			>
				{shortcutItem.label}
			</LinkMenuItem>
		);
	}

	// Check if the item is in the items map
	const item = items[id];

	if (!item) {
		throw new Error(
			`NavItem: No item found for id "${id}". Ensure the item is defined in appItems or globalItems.`,
		);
	}

	if (isCustomItemConfig(item)) {
		// If the item is a custom item, render it directly
		return <item.Component onPeek={() => onPeek?.(id)} isInMoreMenu={true} onClick={onClick} />;
	}

	const moreItem = buildMoreItem({ id, item, onClick, onPeek });
	return <NavItem id={id} item={moreItem} isInMoreMenu />;
}

function buildMoreItem({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: NavItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): NavItemConfig {
	if (
		isExpandableItemConfig(item) ||
		isFlyoutItemConfig(item) ||
		isObjectListFlyoutItemConfig(item)
	) {
		return buildMoreItemFromExpandableOrFlyout({ id, item, onClick, onPeek });
	} else if (isButtonItemConfig(item) || isLinkItemConfig(item)) {
		return buildMoreItemFromButtonOrLink({ item, onClick });
	}

	// If the item is not a button, link, expandable, or flyout, return as is
	return item;
}

function buildMoreItemFromExpandableOrFlyout({
	id,
	item,
	onClick,
	onPeek,
}: {
	id: string;
	item: ExpandableItemConfig | FlyoutItemConfig | ObjectListFlyoutItemConfig;
	onClick?: () => void;
	onPeek?: (id: string) => void;
}): ButtonItemConfig {
	const { elemBefore, label } = item;

	const onClickActual = combineHandlerAndActions(
		// ExpandableItemConfig['onClick'] has a third parameter we're not currently providing
		item.onClick as ButtonItemConfig['onClick'],
		onClick,
		onPeek && (() => onPeek(id)),
	);

	// If the item is in the more menu and is an expandable or flyout item, render as a button with peek
	return {
		type: 'button',
		elemBefore,
		label,
		onClick: onClickActual,
	};
}

function buildMoreItemFromButtonOrLink<T extends ButtonItemConfig | LinkItemConfig>({
	item,
	onClick,
}: {
	item: T;
	onClick?: () => void;
}): T {
	const onClickActual = combineHandlerAndActions(item.onClick, onClick);

	return {
		...item,
		// Remove props that are not appropriate for the more menu
		actions: undefined,
		actionsOnHover: undefined,
		elemAfter: undefined,
		labelElem: undefined,
		onClick: onClickActual,
	};
}

const combineHandlerAndActions =
	<T extends (...args: any) => void>(handler?: T, ...actions: ((() => void) | undefined)[]) =>
	(...args: Parameters<T>) => {
		handler?.(...args);
		actions.forEach((action) => {
			action?.();
		});
	};
